import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchContent, fetchReviews } from '../api/api';
import './About.css';

const StarIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
);

function About() {
  const [about, setAbout] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewPage, setReviewPage] = useState(0);
  const reviewsPerPage = 6;

  useEffect(() => {
    fetchContent('about').then(data => setAbout(data)).catch(() => setAbout({
      title: 'About Green Wheels Scooters',
      content: 'We are committed to providing eco-friendly transportation solutions.',
      mission: 'Our mission is to make cities greener one scooter at a time.',
      values: []
    }));
    fetchReviews().then(data => setReviews(data.filter(r => r.visible !== false))).catch(() => {});
  }, []);

  return (
    <div className="about-page">

      {/* Hero */}
      <div className="about-hero">
        <div className="container">
          <h1 style={{ textAlign: about.titleAlign || 'center' }}>{about.title || 'About Green Wheels Scooters'}</h1>
          <p>Dublin's leading electric scooter & bike store — eco-friendly, affordable, and built for city life.</p>
        </div>
      </div>

      {/* Main content */}
      <div className="container">
        <div className="about-body">

          {about.content && (
            <div className="about-section">
              <p className="about-lead" style={{ textAlign: about.contentAlign || 'left' }}>{about.content}</p>
            </div>
          )}

          {about.mission && (
            <div className="about-section about-mission" style={{ textAlign: about.missionAlign || 'left' }}>
              <div className="about-mission-icon">🌿</div>
              <div>
                <h2>Our Mission</h2>
                <p>{about.mission}</p>
              </div>
            </div>
          )}

          {about.values?.length > 0 && (
            <div className="about-section">
              <h2>Our Values</h2>
              <div className="about-values-grid">
                {about.values.map((value, idx) => (
                  <div key={idx} className="about-value-card">
                    <div className="about-value-icon">✓</div>
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="about-stats">
            {(about.stats || [
              { num: '5000+', label: 'Happy Customers' },
              { num: '100+', label: 'Products' },
              { num: '5\u2605', label: 'Google Rating' },
              { num: '7 Days', label: 'Open Every Week' },
            ]).map((s, i) => (
              <div key={i} className="about-stat">
                <div className="about-stat-num">{s.num}</div>
                <div className="about-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Reviews */}
      {reviews.length > 0 && (
        <div className="about-reviews-section">
          <div className="container">
            <div className="about-reviews-header">
              <div>
                <h2>What Our Customers Say</h2>
                <p>Real reviews from verified Google customers</p>
              </div>
              <div className="about-reviews-badge">
                <div className="about-reviews-stars">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="about-rs"><StarIcon /></div>
                  ))}
                </div>
                <span>Excellent · {reviews.length} Reviews</span>
              </div>
            </div>
            <div className="about-reviews-grid">
              {reviews.slice(reviewPage * reviewsPerPage, (reviewPage + 1) * reviewsPerPage).map((r, i) => (
                <div key={i} className="about-review-card">
                  <div className="about-review-stars">
                    {[1,2,3,4,5].map(s => (
                      <div key={s} className={`about-rs${s > (r.stars || 5) ? ' empty' : ''}`}><StarIcon /></div>
                    ))}
                  </div>
                  {r.title && <p className="about-review-title">{r.title}</p>}
                  <p className="about-review-body">{r.body}</p>
                  <div className="about-review-author">
                    {r.photo && <img src={r.photo} alt={r.author} onError={e => { e.target.style.display = 'none'; }} />}
                    <div>
                      <span className="about-review-name">{r.author}</span>
                      {r.date && <span className="about-review-date">{r.date}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {reviews.length > reviewsPerPage && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 24 }}>
                <button onClick={() => setReviewPage(p => p - 1)} disabled={reviewPage === 0} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', background: reviewPage === 0 ? '#eee' : '#1a1a2e', color: reviewPage === 0 ? '#aaa' : '#fff', fontWeight: 600, cursor: reviewPage === 0 ? 'default' : 'pointer' }}>
                  Prev
                </button>
                <span style={{ fontSize: 14, color: '#555' }}>{reviewPage + 1} / {Math.ceil(reviews.length / reviewsPerPage)}</span>
                <button onClick={() => setReviewPage(p => p + 1)} disabled={(reviewPage + 1) * reviewsPerPage >= reviews.length} style={{ padding: '8px 18px', borderRadius: 20, border: 'none', background: (reviewPage + 1) * reviewsPerPage >= reviews.length ? '#eee' : '#1a1a2e', color: (reviewPage + 1) * reviewsPerPage >= reviews.length ? '#aaa' : '#fff', fontWeight: 600, cursor: (reviewPage + 1) * reviewsPerPage >= reviews.length ? 'default' : 'pointer' }}>
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="about-cta">
        <div className="container">
          <h2>Ready to Go Green?</h2>
          <p>Visit our store or browse our full range of electric scooters and bikes online.</p>
          <div className="about-cta-btns">
            <Link to="/products" className="btn">Shop Now</Link>
            <Link to="/contact" className="btn about-btn-outline">Contact Us</Link>
          </div>
        </div>
      </div>

    </div>
  );
}

export default About;
